/**
 * Slice 23 — Account Settings
 * Owner: install · profile · security · notifications · payments
 * Team + Cleaner: shared basics (profile · security) only
 */

import { useEffect, useRef, useState, type ComponentProps } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ArrowLeft, ChevronRight, Loader2 } from "lucide-react";
import Navbar from "@/components/Navbar";
import { AppLayout } from "@/components/layout/AppLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useViewContext } from "@/contexts/ViewContext";
import { useUserCapabilities } from "@/hooks/useUserCapabilities";
import { useUpdateProfile } from "@/hooks/useUpdateProfile";
import { useBillingProfile, useCreateSetupIntent, useSavePaymentMethod } from "@/hooks/useBillingProfile";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { StripeCardForm } from "@/components/owner/StripeCardForm";
import { InstallAppCard } from "@/components/InstallAppCard";
import { JobAlertsSettings } from "@/components/JobAlertsSettings";
import { LanguageSelector } from "@/components/cleaner/LanguageSelector";
import { CleanerLanguageToggle } from "@/components/cleaner/CleanerLanguageToggle";
import { normalizeCleanerAppLanguage } from "@/hooks/useCleanerAppLanguage";
import { prepareImageFileForUpload, isLikelyImageFile } from "@/lib/prepareImageForUpload";
import { showAccountWorkspaceBlock, isDualRole } from "@/lib/workspaceMode";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";
import { useUpdateProfileLanguage } from "@/hooks/useProfileLanguage";
import {
  LanguageCode,
  getStoredLanguage,
  isValidLanguageCode,
} from "@/lib/languages";

const TIMEZONES = [
  { value: "UTC", label: "UTC" },
  { value: "America/New_York", label: "Eastern (US)" },
  { value: "America/Chicago", label: "Central (US)" },
  { value: "America/Denver", label: "Mountain (US)" },
  { value: "America/Los_Angeles", label: "Pacific (US)" },
  { value: "America/Toronto", label: "Eastern (Canada)" },
  { value: "America/Vancouver", label: "Pacific (Canada)" },
  { value: "Europe/London", label: "London" },
  { value: "Europe/Paris", label: "Paris" },
  { value: "Australia/Sydney", label: "Sydney" },
];

const STRIPE_PUBLISHABLE_KEY =
  import.meta.env.VITE_STRIPE_TEST_PUBLISHABLE_KEY ||
  import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY ||
  "";
const stripePromise = loadStripe(STRIPE_PUBLISHABLE_KEY || undefined);

const fieldClass =
  "mt-0 h-12 rounded-[13px] border-[1.5px] border-[#e6edf1] bg-white px-[15px] text-base font-medium text-[#0f1720] shadow-none focus-visible:ring-[#24a89a]";
const sectionClass =
  "rounded-[18px] border-[1.5px] border-[#e6edf1] bg-white p-5 sm:p-6";
const sectionTitleClass = "text-[19px] font-bold tracking-[-0.4px] text-[#0f1720]";
const sectionHelperClass = "text-[14px] font-medium text-[#64748b]";
const labelClass = "text-[13px] font-bold text-[#64748b]";
const primaryBtnClass =
  "h-12 rounded-[13px] bg-[#24a89a] px-[22px] text-base font-bold text-white shadow-none hover:bg-[#1b8378]";
const outlineBtnClass =
  "h-12 rounded-[13px] border-[1.5px] border-[#e6edf1] bg-white px-[22px] text-base font-bold text-[#0f1720] shadow-none hover:bg-[#f4f7f9]";

type NotifKey =
  | "task_reminder"
  | "invoice_paid"
  | "maintenance_alert"
  | "cleaner_incident_email"
  | "inventory_low_email"
  | "maintenance_issues_email"
  | "cleaning_complete_email"
  | "weekly_summary";

const NOTIF_ROWS: Array<{ key: NotifKey; label: string; sub?: string }> = [
  { key: "task_reminder", label: "Task reminders" },
  { key: "invoice_paid", label: "Invoice paid" },
  { key: "maintenance_alert", label: "Maintenance alerts" },
  {
    key: "cleaner_incident_email",
    label: "Cleaner incident emails",
    sub: "Entry walkthrough issues reported by cleaners",
  },
  {
    key: "inventory_low_email",
    label: "Low-stock inventory emails",
    sub: "When a turnover inventory check finishes below threshold",
  },
  {
    key: "maintenance_issues_email",
    label: "Damage & issue emails",
    sub: "When a cleaner reports broken or damaged items during a clean",
  },
  {
    key: "cleaning_complete_email",
    label: "Cleaning complete emails",
    sub: "When a cleaner marks a turnover as finished",
  },
  { key: "weekly_summary", label: "Weekly summary" },
];

export default function Account() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, profile, loading, signOut } = useAuth();
  const { is_owner, is_cleaner, is_cohost, has_cleaner_assignment, isLoading: capsLoading } = useUserCapabilities();
  const { viewContext, setViewContext } = useViewContext();
  const updateProfile = useUpdateProfile();
  const updateLanguage = useUpdateProfileLanguage();
  const { t, changeLanguage } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const showOwnerStack = is_owner;
  const showWorkspace = showAccountWorkspaceBlock({ is_cleaner, is_owner, is_cohost });
  const dual = isDualRole({ is_cleaner, is_owner, is_cohost, has_cleaner_assignment });
  const jobsCurrent = viewContext === "cleaner";

  const [profileName, setProfileName] = useState("");
  const [phone, setPhone] = useState("");
  const profileLang = (profile as { language?: string } | null)?.language;
  const currentLanguage: LanguageCode = isValidLanguageCode(profileLang ?? "")
    ? (profileLang as LanguageCode)
    : getStoredLanguage();
  const [language, setLanguage] = useState<LanguageCode>(currentLanguage);
  const [timezone, setTimezone] = useState("UTC");
  const [avatarUrl, setAvatarUrl] = useState("");
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [savedProfileFlash, setSavedProfileFlash] = useState(false);
  const [savedPrefsFlash, setSavedPrefsFlash] = useState(false);
  const [notifPrefs, setNotifPrefs] = useState<Record<NotifKey, boolean>>({
    task_reminder: true,
    invoice_paid: true,
    maintenance_alert: true,
    weekly_summary: false,
    cleaner_incident_email: true,
    inventory_low_email: true,
    maintenance_issues_email: true,
    cleaning_complete_email: true,
  });
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [changingPassword, setChangingPassword] = useState(false);

  const { data: billingProfile, isLoading: billingLoading } = useBillingProfile();

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!profile) return;
    setProfileName(profile.full_name ?? [profile.first_name, profile.last_name].filter(Boolean).join(" ") ?? "");
    setPhone(profile.phone ?? "");
    const nextLang = (profile as { language?: string }).language;
    if (isValidLanguageCode(nextLang ?? "")) {
      setLanguage(nextLang as LanguageCode);
    }
    setTimezone(profile.timezone ?? "UTC");
    setAvatarUrl(profile.avatar_url ?? "");
    const prefs = profile.notification_preferences as Record<string, boolean> | undefined;
    if (prefs) {
      setNotifPrefs((p) => ({
        task_reminder: prefs.task_reminder ?? p.task_reminder,
        invoice_paid: prefs.invoice_paid ?? p.invoice_paid,
        maintenance_alert: prefs.maintenance_alert ?? p.maintenance_alert,
        weekly_summary: prefs.weekly_summary ?? p.weekly_summary,
        cleaner_incident_email: prefs.cleaner_incident_email ?? p.cleaner_incident_email,
        inventory_low_email: prefs.inventory_low_email ?? p.inventory_low_email,
        maintenance_issues_email: prefs.maintenance_issues_email ?? p.maintenance_issues_email,
        cleaning_complete_email: prefs.cleaning_complete_email ?? p.cleaning_complete_email,
      }));
    }
  }, [profile]);

  const handleLanguageChange = async (newLang: LanguageCode) => {
    const previous = language;
    setLanguage(newLang);
    changeLanguage(newLang);
    try {
      await updateLanguage.mutateAsync(newLang);
      toast({ title: t("languageUpdated") });
    } catch (e) {
      setLanguage(previous);
      changeLanguage(previous);
      toast({
        title: t("errorUpdatingLanguage"),
        description: (e as Error).message,
        variant: "destructive",
      });
    }
  };

  const handleSaveProfile = async () => {
    try {
      await updateProfile.mutateAsync({
        full_name: profileName.trim() || null,
        phone: phone.trim() || null,
        avatar_url: avatarUrl.trim() || null,
        ...(showOwnerStack ? { timezone: timezone || null } : {}),
      });
      setSavedProfileFlash(true);
      window.setTimeout(() => setSavedProfileFlash(false), 2000);
      toast({ title: "Profile updated" });
    } catch (e) {
      toast({ title: "Failed to save", description: (e as Error).message, variant: "destructive" });
    }
  };

  const handleSaveNotifications = async () => {
    try {
      await updateProfile.mutateAsync({ notification_preferences: notifPrefs });
      setSavedPrefsFlash(true);
      window.setTimeout(() => setSavedPrefsFlash(false), 2000);
      toast({ title: "Notification preferences saved" });
    } catch (e) {
      toast({ title: "Failed to save", variant: "destructive" });
    }
  };

  const handleChangePassword = async () => {
    if (newPassword !== confirmPassword) {
      toast({ title: "Passwords don't match", variant: "destructive" });
      return;
    }
    if (newPassword.length < 8) {
      toast({ title: "Password must be at least 8 characters", variant: "destructive" });
      return;
    }
    setChangingPassword(true);
    try {
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast({ title: "Password updated" });
      setPasswordDialogOpen(false);
      setNewPassword("");
      setConfirmPassword("");
    } catch (e) {
      toast({ title: "Failed to update password", description: (e as Error).message, variant: "destructive" });
    } finally {
      setChangingPassword(false);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/login", { replace: true });
  };

  const handleAvatarFile = async (file: File | null) => {
    if (!file || !user?.id) return;
    if (!isLikelyImageFile(file)) {
      toast({ title: "Choose an image file", variant: "destructive" });
      return;
    }
    setUploadingAvatar(true);
    try {
      const prepared = await prepareImageFileForUpload(file);
      const ext = prepared.name.split(".").pop()?.toLowerCase() || "jpg";
      const path = `${user.id}/avatar-${Date.now()}.${ext}`;
      const { error: uploadError } = await supabase.storage.from("avatars").upload(path, prepared, {
        contentType: prepared.type || "image/jpeg",
        upsert: true,
      });
      if (uploadError) throw uploadError;
      const { data: urlData } = supabase.storage.from("avatars").getPublicUrl(path);
      const publicUrl = urlData.publicUrl;
      setAvatarUrl(publicUrl);
      await updateProfile.mutateAsync({ avatar_url: publicUrl });
      toast({ title: "Photo updated" });
    } catch (e) {
      toast({
        title: "Could not upload photo",
        description: (e as Error).message,
        variant: "destructive",
      });
    } finally {
      setUploadingAvatar(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const isCleaner = profile?.role === "cleaner";

  if (loading || !user || capsLoading) {
    return (
      <AppLayout>
        {!isCleaner ? <Navbar /> : null}
        <div className="flex min-h-[40vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#24a89a]" />
        </div>
      </AppLayout>
    );
  }

  const displayName = profileName.trim() || user.email?.split("@")[0] || "User";
  const initials =
    displayName
      .split(/\s+/)
      .map((s) => s[0])
      .slice(0, 2)
      .join("")
      .toUpperCase() || "?";

  const profileHelper = showOwnerStack
    ? "Photo, name, phone, and timezone"
    : "Photo, name, and phone";

  return (
    <AppLayout>
      {!isCleaner ? <Navbar /> : null}
      <div className="min-h-[calc(100dvh-4rem)] w-full bg-[#f4f7f9]">
        <div className="mx-auto w-full max-w-[720px] px-4 py-6 sm:px-6 sm:py-8">
          <button
            type="button"
            onClick={() => {
              if (isCleaner) navigate("/worker/schedule");
              else navigate(-1);
            }}
            className="mb-5 inline-flex min-h-10 items-center gap-2 rounded-[11px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 text-[15px] font-bold text-[#475569] hover:bg-[#f9fbfc]"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <div className="mb-5 flex flex-col gap-1.5">
            <h1 className="text-[28px] font-bold tracking-[-0.8px] text-[#0f1720] sm:text-[30px]">
              Account Settings
            </h1>
            <p className="text-[15px] font-medium text-[#64748b]">Personal settings only.</p>
          </div>

          <div className="flex flex-col gap-[18px]">
            {showWorkspace ? (
              <div className="overflow-hidden rounded-[16px] border-[1.5px] border-[#e6edf1] bg-white">
                  <div className="border-b-[1.5px] border-[#e6edf1] bg-[#f9fbfc] px-4 py-[13px] text-[13px] font-bold tracking-[0.4px] text-[#64748b]">
                    Workspace
                  </div>
                  {dual ? (
                    <>
                      <button
                        type="button"
                        onClick={() => {
                          setViewContext("owner");
                          navigate("/owner/properties");
                        }}
                        className="flex min-h-[60px] w-full items-center gap-3 border-b-[1.5px] border-[#f0f4f7] px-4 py-3 text-left"
                      >
                        <div className="flex-1 text-[16px] font-bold text-[#0f1720]">My properties</div>
                        {!jobsCurrent ? (
                          <div className="rounded-full bg-[#d7efec] px-[11px] py-[5px] text-[12px] font-bold tracking-[0.4px] text-[#15776d]">
                            Current
                          </div>
                        ) : (
                          <ChevronRight className="h-[18px] w-[18px] text-[#94a3b8]" />
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setViewContext("cleaner");
                          navigate("/worker/schedule");
                        }}
                        className="flex min-h-[60px] w-full items-center gap-3 px-4 py-3 text-left"
                      >
                        <div className="flex-1 text-[16px] font-bold text-[#0f1720]">My jobs</div>
                        {jobsCurrent ? (
                          <div className="rounded-full bg-[#d7efec] px-[11px] py-[5px] text-[12px] font-bold tracking-[0.4px] text-[#15776d]">
                            Current
                          </div>
                        ) : (
                          <ChevronRight className="h-[18px] w-[18px] text-[#94a3b8]" />
                        )}
                      </button>
                    </>
                  ) : (
                    <button
                      type="button"
                      onClick={() => {
                        setViewContext("owner");
                        navigate("/owner/properties");
                      }}
                      className="flex min-h-[60px] w-full items-center gap-3 px-4 py-3 text-left"
                    >
                      <div className="flex flex-1 flex-col gap-0.5">
                        <div className="text-[16px] font-bold text-[#0f1720]">My properties</div>
                        <div className="text-[13px] font-medium text-[#64748b]">Add your own</div>
                      </div>
                      <ChevronRight className="h-[18px] w-[18px] text-[#94a3b8]" />
                    </button>
                  )}
                </div>
            ) : null}

            {/* Language — instant apply; cleaners need this without hunting Profile */}
            <section className={sectionClass}>
              <div className="mb-4 flex flex-col gap-1">
                <h2 className={sectionTitleClass}>{t("languageSettings")}</h2>
                <p className={sectionHelperClass}>{t("languageDescription")}</p>
              </div>
              <div className="flex flex-col gap-2">
                <Label className={labelClass}>{t("preferredLanguage")}</Label>
                {is_cleaner && !is_owner ? (
                  <CleanerLanguageToggle
                    value={normalizeCleanerAppLanguage(language)}
                    disabled={updateLanguage.isPending}
                    onChange={(v) => void handleLanguageChange(v)}
                  />
                ) : (
                  <LanguageSelector
                    value={language}
                    onChange={(v) => void handleLanguageChange(v)}
                    className="w-full"
                  />
                )}
                {updateLanguage.isPending ? (
                  <div className="flex items-center gap-2 text-[14px] font-medium text-[#64748b]">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("updatingLanguage")}
                  </div>
                ) : null}
              </div>
            </section>

            {showOwnerStack ? <InstallAppCard userId={user?.id} /> : null}

            {/* Profile */}
            <section className={sectionClass}>
              <div className="mb-5 flex flex-col gap-1">
                <h2 className={sectionTitleClass}>Profile</h2>
                <p className={sectionHelperClass}>{profileHelper}</p>
              </div>

              <div className="mb-5 flex items-center gap-4 sm:gap-[18px]">
                <div className="flex h-[72px] w-[72px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#e2eeed] text-[25px] font-bold text-[#15776d]">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt="" className="h-full w-full object-cover" />
                  ) : (
                    initials
                  )}
                </div>
                <div className="flex flex-col items-start gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,.heic,.heif"
                    className="hidden"
                    onChange={(e) => void handleAvatarFile(e.target.files?.[0] ?? null)}
                  />
                  <button
                    type="button"
                    disabled={uploadingAvatar}
                    onClick={() => fileInputRef.current?.click()}
                    className={cn(outlineBtnClass, "min-h-12")}
                  >
                    {uploadingAvatar ? (
                      <>
                        <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
                        Uploading…
                      </>
                    ) : (
                      "Change photo"
                    )}
                  </button>
                  <p className="text-[13px] font-medium text-[#64748b]">
                    Upload from your phone or computer.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-4">
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>Name</Label>
                  <Input
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Your name"
                    className={fieldClass}
                  />
                </div>
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>Email</Label>
                  <Input
                    value={user.email ?? ""}
                    disabled
                    className={cn(fieldClass, "bg-[#f4f7f9] text-[#64748b]")}
                  />
                  <p className="text-[13px] font-medium text-[#64748b]">
                    Email is managed by your sign-in provider.
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <Label className={labelClass}>Phone</Label>
                  <Input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    placeholder="+1 234 567 8900"
                    className={fieldClass}
                  />
                </div>
                {showOwnerStack ? (
                  <div className="flex flex-col gap-2 sm:col-span-2">
                    <Label className={labelClass}>Timezone</Label>
                    <Select value={timezone} onValueChange={setTimezone}>
                      <SelectTrigger className={cn(fieldClass, "w-full")}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {TIMEZONES.map((tz) => (
                          <SelectItem key={tz.value} value={tz.value}>
                            {tz.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ) : null}
              </div>

              <div className="mt-5 flex items-center gap-3.5">
                <Button
                  type="button"
                  onClick={() => void handleSaveProfile()}
                  disabled={updateProfile.isPending}
                  className={primaryBtnClass}
                >
                  {updateProfile.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                  Save profile
                </Button>
                {savedProfileFlash ? (
                  <span className="text-[14px] font-bold text-[#15776d]">Saved</span>
                ) : null}
              </div>
            </section>

            {/* Security */}
            <section className={sectionClass}>
              <div className="mb-4 flex flex-col gap-1">
                <h2 className={sectionTitleClass}>Security</h2>
                <p className={sectionHelperClass}>Change password and sign out</p>
              </div>
              <div className="flex flex-wrap gap-2.5">
                <button
                  type="button"
                  onClick={() => setPasswordDialogOpen(true)}
                  className={outlineBtnClass}
                >
                  Change password
                </button>
                <button
                  type="button"
                  onClick={() => void handleSignOut()}
                  className="h-12 rounded-[13px] border-[1.5px] border-[#e6c0b6] bg-white px-[22px] text-base font-bold text-[#b4442f] hover:bg-[#fdf6f4]"
                >
                  Sign out
                </button>
              </div>
              <p className="mt-4 text-[13px] font-medium text-[#64748b]">
                Signs you out on this device. Changing your password will sign out other sessions.
              </p>
            </section>

            {/* Job push alerts — always on Account (cleaner Profile tab routes here) */}
            {user?.id ? (
              <section className={sectionClass}>
                <div className="mb-4 flex flex-col gap-1">
                  <h2 className={sectionTitleClass}>Job alerts</h2>
                  <p className={sectionHelperClass}>
                    Push when checkout changes on jobs assigned to you
                  </p>
                </div>
                <JobAlertsSettings userId={user.id} embedded />
              </section>
            ) : null}

            {showOwnerStack ? (
              <>
                {/* Notifications */}
                <section className={sectionClass}>
                  <div className="mb-4 flex flex-col gap-1">
                    <h2 className={sectionTitleClass}>Notifications</h2>
                    <p className={sectionHelperClass}>Email notifications by event</p>
                  </div>
                  <div className="flex flex-col">
                    {NOTIF_ROWS.map((row, i) => (
                      <div
                        key={row.key}
                        className={cn(
                          "flex items-center justify-between gap-4 py-3.5",
                          i < NOTIF_ROWS.length - 1 && "border-b border-[#eef2f5]",
                        )}
                      >
                        <div className="flex min-w-0 flex-col gap-0.5 pr-4">
                          <span className="text-base font-medium text-[#0f1720]">{row.label}</span>
                          {row.sub ? (
                            <span className="text-[13px] font-medium text-[#64748b]">{row.sub}</span>
                          ) : null}
                        </div>
                        <Switch
                          checked={notifPrefs[row.key]}
                          onCheckedChange={(c) =>
                            setNotifPrefs((p) => ({ ...p, [row.key]: c }))
                          }
                        />
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex items-center gap-3.5">
                    <Button
                      type="button"
                      onClick={() => void handleSaveNotifications()}
                      disabled={updateProfile.isPending}
                      className={primaryBtnClass}
                    >
                      {updateProfile.isPending ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : null}
                      Save preferences
                    </Button>
                    {savedPrefsFlash ? (
                      <span className="text-[14px] font-bold text-[#15776d]">Saved</span>
                    ) : null}
                  </div>
                </section>

                {/* Payments */}
                <section className={sectionClass}>
                  <div className="mb-4 flex flex-col gap-1">
                    <h2 className={sectionTitleClass}>Payments</h2>
                    <p className={sectionHelperClass}>
                      Card on file for paying cleaners and invoices
                    </p>
                  </div>
                  <OwnerPaymentsBlock
                    billingProfile={billingProfile}
                    billingLoading={billingLoading}
                    stripePromise={stripePromise}
                  />
                </section>
              </>
            ) : null}
          </div>
        </div>
      </div>

      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="gap-0 overflow-hidden rounded-[22px] border-[#e6edf1] p-0 sm:max-w-[440px]">
          <div className="flex flex-col gap-[18px] p-7">
            <DialogHeader className="space-y-0 text-left">
              <DialogTitle className="text-2xl font-bold tracking-[-0.6px] text-[#0f1720]">
                Change password
              </DialogTitle>
              <DialogDescription className="sr-only">
                Enter a new password for your account.
              </DialogDescription>
            </DialogHeader>
            <div className="flex flex-col gap-2">
              <Label className={labelClass}>New password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="At least 8 characters"
                className={cn(fieldClass, "border-[#24a89a]")}
              />
            </div>
            <div className="flex flex-col gap-2">
              <Label className={labelClass}>Confirm new password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repeat new password"
                className={fieldClass}
              />
            </div>
            <p className="text-[13px] font-medium text-[#64748b]">
              Signs you out on this device. Changing your password will sign out other sessions.
            </p>
            <DialogFooter className="flex-row gap-2.5 sm:space-x-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordDialogOpen(false)}
                className={cn(outlineBtnClass, "flex-1")}
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={() => void handleChangePassword()}
                disabled={changingPassword}
                className={cn(primaryBtnClass, "flex-1")}
              >
                {changingPassword ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Change password
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function OwnerPaymentsBlock({
  billingProfile,
  billingLoading,
  stripePromise,
}: {
  billingProfile: {
    card_brand?: string | null;
    card_last4?: string | null;
    card_exp_month?: number | null;
    card_exp_year?: number | null;
  } | null | undefined;
  billingLoading: boolean;
  stripePromise: ComponentProps<typeof Elements>["stripe"];
}) {
  const { toast } = useToast();
  const createSetupIntent = useCreateSetupIntent();
  const savePaymentMethod = useSavePaymentMethod();
  const [cardDialogOpen, setCardDialogOpen] = useState(false);
  const [cardSetupData, setCardSetupData] = useState<{
    clientSecret: string;
    setupIntentId: string;
    stripe_mode: "test" | "live";
  } | null>(null);

  const cardDisplay = billingProfile?.card_last4
    ? `${billingProfile.card_brand ?? "Card"} •••• ${billingProfile.card_last4}`
    : null;
  const exp =
    billingProfile?.card_exp_month != null && billingProfile?.card_exp_year != null
      ? `${String(billingProfile.card_exp_month).padStart(2, "0")} / ${String(billingProfile.card_exp_year).slice(-2)}`
      : null;

  const stripeMode: "test" | "live" = STRIPE_PUBLISHABLE_KEY.startsWith("pk_test_") ? "test" : "live";

  const openCardDialog = async () => {
    try {
      const res = await createSetupIntent.mutateAsync({ stripe_mode: stripeMode });
      const { client_secret, setup_intent_id, stripe_mode: mode } = res;
      if (!client_secret || !setup_intent_id) throw new Error("Missing client_secret or setup_intent_id");
      setCardSetupData({
        clientSecret: client_secret,
        setupIntentId: setup_intent_id,
        stripe_mode: mode === "live" ? "live" : "test",
      });
      setCardDialogOpen(true);
    } catch (e) {
      toast({ title: "Could not open card form", description: (e as Error).message, variant: "destructive" });
    }
  };

  const handleCardSuccess = () => {
    setCardDialogOpen(false);
    setCardSetupData(null);
    toast({ title: "Card saved" });
  };

  if (billingLoading) {
    return <p className="text-[15px] font-medium text-[#64748b]">Loading…</p>;
  }

  return (
    <div className="space-y-3.5">
      {cardDisplay ? (
        <div className="flex min-h-16 items-center gap-3.5 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-[#f9fbfc] px-[18px]">
          <div className="h-[30px] w-11 shrink-0 rounded-md bg-[#d5e2e9]" />
          <div className="flex min-w-0 flex-1 flex-col gap-0.5">
            <p className="text-base font-bold text-[#0f1720]">{cardDisplay}</p>
            {exp ? (
              <p className="text-[13px] font-medium text-[#64748b]">Expires {exp}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={() => void openCardDialog()}
            disabled={createSetupIntent.isPending}
            className={cn(outlineBtnClass, "shrink-0 px-[18px] text-[15px]")}
          >
            {createSetupIntent.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Update"
            )}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-start gap-3.5 rounded-[14px] border-[1.5px] border-dashed border-[#d7e0e6] bg-[#f9fbfc] px-[18px] py-[26px]">
          <p className="text-[15px] font-medium text-[#64748b]">
            No card on file. Add one to pay cleaners and invoices.
          </p>
          <button
            type="button"
            onClick={() => void openCardDialog()}
            disabled={createSetupIntent.isPending}
            className={primaryBtnClass}
          >
            {createSetupIntent.isPending ? (
              <Loader2 className="mr-2 inline h-4 w-4 animate-spin" />
            ) : null}
            Add card
          </button>
        </div>
      )}

      <Dialog
        open={cardDialogOpen}
        onOpenChange={(open) => {
          if (!open) {
            setCardDialogOpen(false);
            setCardSetupData(null);
          }
        }}
      >
        <DialogContent className="rounded-[22px] sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{cardDisplay ? "Update card" : "Add card"}</DialogTitle>
            <DialogDescription>
              Card is used to pay cleaners and invoices. Secured by Stripe.
            </DialogDescription>
          </DialogHeader>
          {cardSetupData && stripePromise ? (
            <Elements stripe={stripePromise}>
              <StripeCardForm
                clientSecret={cardSetupData.clientSecret}
                setupIntentId={cardSetupData.setupIntentId}
                stripeMode={cardSetupData.stripe_mode}
                onSuccess={handleCardSuccess}
                onError={(message) =>
                  toast({ title: "Card error", description: message, variant: "destructive" })
                }
                savePaymentMethod={savePaymentMethod.mutateAsync}
              />
            </Elements>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
